import React from 'react'
import isEmpty from 'lodash/isEmpty'
import { useToggle } from 'react-use'
import type { RouteDef } from '@ztechapps/common.contract'
import { blogPermissions } from '@ztechapps/blog.common'
import { useError, useHistory, useRole } from '@ztechapps/common.hook'
import { useInject, WebuiIocKey } from '@ztechapps/webui.contract'
import { BusyButton } from '@ztechapps/webui.button'
import { RichTextDisplay } from '@ztechapps/webui.input'
import { Modal, ModalDragHandle } from '@ztechapps/webui.modal'
import { useBlogPreviewSlug } from '../hooks/useBlogPreviewSlug'
import { NO_EDIT_PERMISSION_REASON } from '../PermissionReasons'
import { useWhatsNewContext } from './Context'

export const WhatsNewModal = () => {
  const { post, open, closeWhatsNew } = useWhatsNewContext()
  const history = useHistory()
  const blogPreviewRoute = useInject<RouteDef>(WebuiIocKey.routes.blogPreview)
  const { hasRole: canEdit } = useRole({ roleName: blogPermissions.canEdit.value })
  const { getPreviewSlug } = useBlogPreviewSlug({ blogId: post?._id ?? '', slug: post?.slug ?? '' })
  const { onError } = useError()
  const [opening, setOpening] = useToggle(false)

  if (!post) return null

  const onOpenFullPost = async () => {
    setOpening(true)
    try {
      history.push(blogPreviewRoute.altPath({ slug: await getPreviewSlug() }))
    } catch (e) {
      onError(e)
    } finally {
      setOpening(false)
    }
  }

  return (
    <Modal show={open} onHide={closeWhatsNew} size="lg" scrollable trigger={() => null}>
      <ModalDragHandle>
        <span className="fs-5 fw-semibold">What&apos;s New</span>
      </ModalDragHandle>
      <Modal.Body>
        <h4>{post.title}</h4>
        {/* trustedSource: release notes are authored by staff in this same admin app, not attacker-reachable */}
        <RichTextDisplay contentHtml={post.contentHtml ?? ''} trustedSource />
      </Modal.Body>
      <Modal.Footer>
        <BusyButton
          variant="outline-secondary"
          icon="external-link"
          text="Open full post"
          isBusy={opening}
          // A slug-less post needs one saved first, which takes blog.canEdit.
          disabledReason={isEmpty(post.slug) && !canEdit ? NO_EDIT_PERMISSION_REASON : undefined}
          onClick={onOpenFullPost}
        />
        <BusyButton text="Close" onClick={closeWhatsNew} />
      </Modal.Footer>
    </Modal>
  )
}
